/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/entryDraft.ts
 * FILE_ID: UI-KANBAN-ENTRIES-DRAFT-001
 * PURPOSE: Pure draft-row model for the drawer's Entries table — the
 *          in-editor row shape plus the I→O transforms to the
 *          `createCardEntries` wire payload.
 * WHY: ARC-3 (U17) — "quick excel-table-like entry … creating
 *      essentially 'new' cards / sub-card-like structure underneath".
 *      Keeping the row math pure (no React, no fetch) makes the mint
 *      payload unit-testable and gives MR5's paste-from-Excel a single
 *      place to land TSV parsing.
 * FLOW:
 *   EntryDraftRow[] (editor state)
 *     → draftsToRowInputs → { rows: CardEntryRowInput[] } | error
 * RELATED:
 *   - api/payloads-cards.ts: CardEntryRowInput (the wire contract)
 *   - CardEntriesSection.tsx: the table UI consuming these helpers
 */

import type { CardEntryRowInput } from '../../../api/payloads-cards'

/** One editable Entries-table row. `key` is a client-only identity for
 *  React lists; `amountText` stays a string until submit so partial
 *  typing ("12.", "") never fights the input. `followsPrev` marks
 *  "this happens next" — chained to the nearest NON-BLANK row above. */
export interface EntryDraftRow {
  key: string
  teammate_id: number | null
  action: string
  what: string
  /** ISO `YYYY-MM-DD` or '' (native date input value). */
  date: string
  /** Dollars as typed, e.g. "1500" / "1,500.50" / ''. */
  amountText: string
  followsPrev: boolean
}

let draftSeq = 0

export function emptyDraftRow(): EntryDraftRow {
  draftSeq += 1
  return {
    key: `draft-${draftSeq}`,
    teammate_id: null,
    action: '',
    what: '',
    date: '',
    amountText: '',
    followsPrev: false,
  }
}

/** A row the operator never touched — skipped at submit so a trailing
 *  empty "next row" doesn't 400 the whole batch. */
export function draftIsBlank(row: EntryDraftRow): boolean {
  return (
    !row.action.trim()
    && !row.what.trim()
    && !row.date
    && !row.amountText.trim()
    && row.teammate_id == null
  )
}

/** "1,500.50" → 150050 (cents, floored to the cent). Returns null for
 *  blank input; NaN-producing garbage returns an error string instead
 *  of a value so the UI can point at the exact row. */
export function parseAmountToCents(
  text: string,
): { cents: number | null } | { error: string } {
  const trimmed = text.trim()
  if (!trimmed) return { cents: null }
  const num = Number(trimmed.replace(/[$,\s]/g, ''))
  if (!Number.isFinite(num) || num < 0) {
    return { error: `"${text}" is not a valid amount.` }
  }
  return { cents: Math.round(num * 100) }
}

export type DraftsResult =
  | { ok: true; rows: CardEntryRowInput[] }
  | { ok: false; error: string }

/** Editor rows → wire payload. Blank rows are dropped; `followsPrev`
 *  resolves to the batch index of the nearest non-blank row above
 *  (a `followsPrev` on the first surviving row is silently ignored —
 *  there is nothing to follow). */
export function draftsToRowInputs(drafts: EntryDraftRow[]): DraftsResult {
  const rows: CardEntryRowInput[] = []
  for (let i = 0; i < drafts.length; i += 1) {
    const d = drafts[i]
    if (draftIsBlank(d)) continue
    const action = d.action.trim()
    if (!action) {
      return { ok: false, error: `Row ${i + 1} needs an action.` }
    }
    const amount = parseAmountToCents(d.amountText)
    if ('error' in amount) {
      return { ok: false, error: `Row ${i + 1}: ${amount.error}` }
    }
    rows.push({
      action,
      what: d.what.trim() || undefined,
      teammate_id: d.teammate_id,
      date: d.date || null,
      amount_cents: amount.cents,
      follows_index: d.followsPrev && rows.length > 0 ? rows.length - 1 : null,
    })
  }
  if (rows.length === 0) {
    return { ok: false, error: 'Nothing to save — add at least one row.' }
  }
  return { ok: true, rows }
}
